import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import { useState } from "react";
import { toast } from "sonner";

import { useUpsertDescription } from "@/hooks/api/useProperties";
import type { components } from "@/types/api";

type Description = components["schemas"]["DescriptionResponse"];

const LANGS: { code: "nl" | "en"; label: string }[] = [
  { code: "nl", label: "Nederlands" },
  { code: "en", label: "English" },
];

export function DescriptionsCard({
  propertyId,
  descriptions,
}: {
  propertyId: string;
  descriptions: Description[];
}) {
  const upsert = useUpsertDescription(propertyId);
  const [bodies, setBodies] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const { code } of LANGS) {
      init[code] = descriptions.find((d) => d.language === code)?.body ?? "";
    }
    return init;
  });

  const save = (code: "nl" | "en") => {
    upsert.mutate(
      { language: code, body: bodies[code] },
      {
        onSuccess: () => toast.success("Description saved"),
        onError: () => toast.error("Could not save description"),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Descriptions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {LANGS.map(({ code, label }) => (
          <div key={code} className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
              </p>
              <Button
                size="sm"
                variant="outline"
                disabled={upsert.isPending}
                onClick={() => save(code)}
              >
                Save
              </Button>
            </div>
            <textarea
              value={bodies[code]}
              onChange={(e) => setBodies({ ...bodies, [code]: e.target.value })}
              rows={10}
              placeholder={`${label} listing description…`}
              className="min-h-48 w-full resize-y rounded-md border border-input bg-card p-3 text-sm leading-relaxed outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
